import { IFundingBoard } from './dofinansowanieModel';
import {
  useFundingNotifications,
  useFundingWatchHealth,
} from './useFundingAutomation';

/**
 * «Требует действия» — очереди, ждущие решения человека (спека 0055).
 *
 * 🔴 Живёт ЗДЕСЬ, а не внутри вкладки, ровно по той причине, по которой в
 * этом домене уже дважды ломались счётчики: число на вкладке и список внутри
 * неё обязаны считаться ОДНОЙ функцией. Посчитай их порознь — вкладка обещает
 * «· 5», а внутри четыре строки, и человек резонно перестаёт верить обеим
 * (`counter-must-match-the-button`).
 *
 * ⚠️ Строки НЕ складываются в «сколько всего дел». Один кандидат попадает и в
 * «не подошёл никто», и в «неоднозначное совпадение»; один оператор — и в
 * «нет адреса», и в «ни разу не проверялся». Число на вкладке — это
 * количество ЖИВЫХ ОЧЕРЕДЕЙ, а не задач.
 */

/** Куда ведёт строка: вкладка плюс фильтры, с которых очередь начинается. */
export interface IActionQueueRow {
  /** Ключ подписи и значка; он же ключ i18n. */
  key: string;
  count: number;
  tab: string;
  params?: Record<string, string>;
  /** `danger` — стоят люди; `warn` — работа; `neutral` — уборка. */
  tone: 'danger' | 'warn' | 'neutral';
  /**
   * Список присмотра, а не очередь дел (задача #105): отмеченные «повышенным
   * вниманием» остаются отмеченными неделями. Строка показывается, но точку
   * на вкладке НЕ зажигает — иначе та горела бы, пока отмечен хоть один
   * человек, и перестала бы значить «есть работа».
   */
  watch?: boolean;
}

/**
 * `boardSettled` — доска ответила (или отказала). До этого все её числа —
 * дефолтные нули, и без флага вкладка уверенно говорила «очередей нет».
 */
export const useActionQueue = (board: IFundingBoard, boardSettled = true) => {
  // Здоровье слежения считает СЕРВЕР одним числом: сложить строки сводки
  // нельзя — причины пересекаются, а имена в них обрезаны потолком.
  const { brokenOperators, settled: healthSettled } = useFundingWatchHealth();
  // Письма с ошибкой — единственный счётчик, которого не было на доске.
  // Берём существующим запросом журнала: `limit: 1` ради `total`.
  const { total: failedMails, settled: mailsSettled } = useFundingNotifications(
    {
      status: 'failed',
      limit: 1,
    },
  );

  /**
   * Порядок несущий: сверху то, где стоят люди, снизу — уборка.
   *
   * Заявка без оператора — человек уже написал нам и ждёт ответа. Кандидат,
   * которому не подошёл никто, — следующий по тяжести: его нельзя двинуть
   * вообще. Изменившаяся страница — вход в появление набора. География и
   * здоровье робота чинятся когда угодно и никого не держат.
   */
  const rows: IActionQueueRow[] = [
    {
      key: 'submissionsNeedOperator',
      count: board.submissionsNeedOperator,
      tab: 'submissions',
      params: { state: 'needs_operator' },
      tone: 'danger',
    },
    {
      /**
       * 🔴 Строк было две — «не подошёл ни один оператор» и «подошло
       * несколько». Обе считались по M2M `candidate.operators`, журналу
       * «кого рассматривали», а не по правилам подбора. Замерено: на 200
       * свежих кандидатах два числа не совпали НИ РАЗУ, а по правилам из 136
       * кандидатов без оператора пустой список не получается ни у кого
       * (медиана 6). То есть очередь называла самым тяжёлым случаем
       * положение, которого не существует.
       *
       * Осталось то, что эта таблица умеет посчитать честно и что можно
       * пересчитать глазами на вкладке: у кого нет оператора.
       */
      key: 'operatorMissing',
      count: board.operatorMissing,
      tab: 'candidates',
      params: { operatorMatch: 'missing' },
      tone: 'danger',
    },
    {
      key: 'pageChanged',
      count: board.pageChangedOperators,
      tab: 'operators',
      params: { segment: 'changed' },
      tone: 'warn',
    },
    {
      key: 'ambiguous',
      count: board.ambiguousMatches,
      tab: 'candidates',
      params: { match: 'ambiguous' },
      tone: 'warn',
    },
    {
      // ТЗ 0111 (#151): несколько открытых заявок одного человека. Раньше
      // они прятались за «неоднозначным совпадением», хотя аккаунт один.
      key: 'duplicates',
      count: board.duplicateSubmissions,
      tab: 'candidates',
      params: { duplicates: '1' },
      tone: 'warn',
    },
    {
      // Задача #105: кого сотрудник попросил держать на контроле. Перед
      // стартом набора робот заводит задачу.
      //
      // ⚠️ Число — популяция робота (отмечен И открыт И не в архиве), а
      // список `?attention=1` — все отмеченные, с закрытыми. Фильтра «только
      // открытые» у вкладки нет (сегменты — группы воронки, «открытые» среди
      // них не бывает), поэтому подпись называет число «открытых: N», а
      // закрытые в списке видны своим сегментом.
      key: 'attention',
      count: board.needsAttention,
      tab: 'candidates',
      params: { attention: '1' },
      tone: 'warn',
      watch: true,
    },
    {
      key: 'geo',
      count: board.unresolvedPlaces,
      tab: 'geo',
      tone: 'neutral',
    },
    {
      key: 'mailFailed',
      count: failedMails,
      tab: 'mail',
      params: { status: 'failed' },
      tone: 'neutral',
    },
    {
      key: 'watchBroken',
      count: brokenOperators,
      tab: 'runs',
      tone: 'neutral',
    },
  ];

  // Очередь с нулём не показывается: строка «0» — шум, а не спокойствие. Она
  // заставляет прочитать и отбросить, и так восемь раз подряд.
  const shown = rows.filter((row) => row.count > 0);
  return {
    rows: shown,
    /**
     * Есть ли РАБОТА — точка на вкладке. Считается здесь же, из тех же строк:
     * предикат, посчитанный на странице отдельно, разъехался бы со списком.
     */
    hasWork: shown.some((row) => !row.watch),
    /** Хоть один источник ещё не ответил — «пусто» пока не ответ. */
    loading: !boardSettled || !healthSettled || !mailsSettled,
  };
};
